import React, {Fragment, useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {useTranslate} from '@computerrock/formation-i18n';
import {calendarIcon, ContentBlock, ContentItem, DateField, Form, InputField, TextAreaField, InputCounter, useStyles} from '@ace-de/ui-components';

const AccommodationSubForm = props => {
    const {cx} = useStyles();
    const {service} = props;
    const {createTranslateShorthand, translate} = useTranslate();
    const translateForm = createTranslateShorthand('accommodation_form');
    const [errors, setErrors] = useState({
        numberOfPeople: false,
        accommodationName: false,
        startDateTime: false,
        endDateTime: false,
    });

    const handleErrors = (field, value) => {
        setErrors(prevState => ({
            ...prevState,
            ...(field !== 'startDateTime'
                ? {
                    [field]: field === 'endDateTime'
                        ? !value?.endDateTime
                        || (value?.startDateTime
                            && moment(value.endDateTime, ['YYYY-MM-DD', 'DD.MM.YYYY'])
                                .isSameOrBefore(moment(value.startDateTime, ['YYYY-MM-DD', 'DD.MM.YYYY'])))
                        : !value,
                } : {
                    startDateTime: !value?.startDateTime,
                    endDateTime: !value?.endDateTime
                        || (value?.startDateTime
                            && moment(value.endDateTime, ['YYYY-MM-DD', 'DD.MM.YYYY'])
                                .isSameOrBefore(moment(value.startDateTime, ['YYYY-MM-DD', 'DD.MM.YYYY']))),
                }),
        }));
    };
    return (
        <Form name="accommodation">
            {formValues => {
                const minEndDate = formValues?.startDateTime || service?.startDateTime || '';
                const formattedMinEndDate = moment(minEndDate, ['YYYY-MM-DD', 'DD.MM.YYYY']).add(1, 'days').format('DD.MM.YYYY');

                return (
                    <Fragment>
                        <h3
                            className={cx([
                                'global!ace-u-typography--variant-h3',
                                'global!ace-u-margin--small-bottom-24',
                            ])}
                        >
                            {translateForm('title.add_details')}
                        </h3>
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('title.period_of_accommodation')}
                        </p>
                        <ContentBlock className={cx('global!ace-u-margin--small-bottom-24')}>
                            <ContentItem className={cx('ace-c-content-item--base-span-6')}>
                                <DateField
                                    name="startDateTime"
                                    label={translateForm('date_field_label.stay_start_date')}
                                    icon={calendarIcon}
                                    format="DD.MM.YYYY"
                                    className={cx('global!ace-u-width--full')}
                                    value={service?.startDateTime || ''}
                                    onBlur={value => handleErrors('startDateTime', {
                                        startDateTime: value,
                                        endDateTime: formValues?.endDateTime || service?.endDateTime || '',
                                    })}
                                    errors={errors.startDateTime ? [translate('global.error.empty_mandatory_field')] : []}
                                />
                            </ContentItem>
                            <ContentItem className={cx('ace-c-content-item--base-span-6')}>
                                <DateField
                                    name="endDateTime"
                                    label={translateForm('date_field_label.stay_end_date')}
                                    icon={calendarIcon}
                                    format="DD.MM.YYYY"
                                    className={cx('global!ace-u-width--full')}
                                    value={service?.endDateTime || ''}
                                    onBlur={value => handleErrors('endDateTime', {
                                        endDateTime: value,
                                        startDateTime: formValues?.startDateTime || service?.startDateTime || '',
                                    })}
                                    errors={errors.endDateTime ? [translate('global.error.empty_mandatory_field')] : []}
                                    minDate={formattedMinEndDate}
                                />
                            </ContentItem>
                        </ContentBlock>
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('title.number_of_people')}
                        </p>
                        <InputCounter
                            className={cx([
                                'global!ace-u-margin--small-bottom-32',
                            ])}
                            name="numberOfPeople"
                            value={service?.numberOfPeople || ''}
                            onBlur={value => handleErrors('numberOfPeople', value)}
                            errors={errors.numberOfPeople ? [translate('global.error.empty_mandatory_field')] : []}
                        />
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('title.place_of_accommodation')}
                        </p>
                        <InputField
                            placeholder={translateForm('input_placeholder.accommodation_name')}
                            name="name"
                            label={translateForm('input_label.accommodation_name')}
                            className={cx([
                                'global!ace-u-width--full',
                                'global!ace-u-margin--x-large-bottom-32',
                                'global!ace-u-margin--small-bottom-24',
                            ])}
                            value={service?.name || ''}
                            onBlur={value => handleErrors('accommodationName', value)}
                            errors={errors.accommodationName ? [translate('global.error.empty_mandatory_field')] : []}
                        />
                        <p
                            className={cx([
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-margin--small-bottom-16',
                            ])}
                        >
                            {translateForm('title.comment')}
                        </p>
                        <TextAreaField
                            placeholder={translateForm('text_area_placeholder.description')}
                            name="description"
                            value={service?.description || ''}
                            maxLength={250}
                            isResizable={false}
                            className={cx([
                                'ace-c-text-area--small',
                                'global!ace-u-margin--small-bottom-64',
                                'global!ace-u-width--full',
                            ])}
                            label={translateForm('text_area_label.description')}
                        />
                    </Fragment>
                );
            }}
        </Form>
    );
};

AccommodationSubForm.propTypes = {
    service: PropTypes.object.isRequired,
};

export default AccommodationSubForm;
